import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { aplicarTemplateTheme } from "./aplicarTemplateTheme";
import {
    detectarContentEditMode,
    refrescarTemplateConfigDesdeAPI,
    type TemplateConfigInicial,
} from "./templateConfigLoader";
import type {
    BloqueHomeDTO,
    FaqsConfigDTO,
    IdentidadTiendaDTO,
    TiendaBaseConfigDTO,
    TiendaContenidoDTO,
} from "./types";
import { derivarCamposEditables, derivarCamposEstilo } from "./editableFields";
import type { CampoEditable } from "./editableFields";
import {
    crearMensajeEditor,
    enviarMensajeAlAdministrador,
    esMensajeAdministradorATienda,
    type BloqueDeseleccionadoMensaje,
    type BloqueSeleccionadoMensaje,
    type ContenidoActualMensaje,
    type EditorListoMensaje,
} from "./templateEditorProtocol";

const VCHTYPE_IDENTIDAD = "identidad";

function obtenerOrigenPadre(): string | null {
    if (typeof document === "undefined" || !document.referrer) return null;
    try {
        return new URL(document.referrer).origin;
    } catch {
        return null;
    }
}

function coincideBloque(
    bloque: BloqueHomeDTO,
    uniIdBloque: string | undefined,
    vchTipo: string,
    intOrden: number,
): boolean {
    return uniIdBloque
        ? bloque.uniIdBloque === uniIdBloque
        : bloque.vchTipo === vchTipo && bloque.intOrden === intOrden;
}

const CAMPOS_IDENTIDAD: CampoEditable[] = [
    { key: "vchTextoPromo", label: "Texto de la barra", tipo: "texto" },
    { key: "vchNombreTienda", label: "Nombre de la tienda", tipo: "texto" },
    { key: "vchDescripcion", label: "Descripción", tipo: "textarea" },
    { key: "vchWhatsappMensaje", label: "Mensaje de WhatsApp", tipo: "texto" },
    { key: "vchLabelCatalogo", label: "Nombre en menú ('Catálogo')", tipo: "texto" },
];

interface TemplateConfigContextValue {
    config: TiendaBaseConfigDTO;
    bitContentEditMode: boolean;
}

const TemplateConfigContext = createContext<TemplateConfigContextValue | null>(null);

export const TemplateConfigProvider: React.FC<{
    configInicial: TemplateConfigInicial;
    children: React.ReactNode;
}> = ({ configInicial, children }) => {
    const [config, setConfig] = useState(configInicial.config);
    const [bitContentEditMode] = useState(() => detectarContentEditMode());
    const [vchOrigenPadre] = useState(obtenerOrigenPadre);
    const configRef = useRef(config);
    configRef.current = config;

    useEffect(() => {
        aplicarTemplateTheme(config);
    }, [config]);

    useEffect(() => {
        if (configInicial.bitPreview || bitContentEditMode) return;

        let bitCancelado = false;

        refrescarTemplateConfigDesdeAPI(configInicial).then((configRefrescada) => {
            if (bitCancelado || !configRefrescada) return;
            setConfig(configRefrescada);
        });

        return () => {
            bitCancelado = true;
        };
    }, [configInicial, bitContentEditMode]);

    const actualizarCampo = useCallback(
        (
            uniIdBloque: string | undefined,
            vchTipo: string,
            intOrden: number,
            campo: string,
            valor: string,
        ) => {
            if (vchTipo === VCHTYPE_IDENTIDAD) {
                setConfig((prev) => ({
                    ...prev,
                    identidad: { ...prev.identidad, [campo]: valor } as IdentidadTiendaDTO,
                }));
                return;
            }
            if (vchTipo === "faqs") {
                setConfig((prev) => ({
                    ...prev,
                    faqsConfig: {
                        ...prev.faqsConfig,
                        [campo]: campo === "bitVisible" ? valor === "true" : valor,
                    } as FaqsConfigDTO,
                }));
                return;
            }
            setConfig((prev) => ({
                ...prev,
                bloquesHome: prev.bloquesHome.map((b) => {
                    if (!coincideBloque(b, uniIdBloque, vchTipo, intOrden)) return b;
                    if (campo === "bitActivo") {
                        return { ...b, bitActivo: valor === "true" };
                    }
                    const valorOriginal = (b.config as Record<string, unknown>)[campo];
                    const valorCoercido =
                        typeof valorOriginal === "number"
                            ? Number(valor)
                            : typeof valorOriginal === "boolean"
                              ? valor === "true"
                              : valor;
                    return { ...b, config: { ...b.config, [campo]: valorCoercido } };
                }),
            }));
        },
        [],
    );

    useEffect(() => {
        if (!bitContentEditMode) return;

        const handleMessage = (event: MessageEvent) => {
            if (event.source !== window.parent) return;
            if (vchOrigenPadre && event.origin !== vchOrigenPadre) return;
            if (!esMensajeAdministradorATienda(event.data)) return;

            const mensaje = event.data;

            if (mensaje.tipo === "ACTUALIZAR_CAMPO") {
                actualizarCampo(
                    mensaje.uniIdBloque,
                    mensaje.vchTipo,
                    mensaje.intOrden,
                    mensaje.campo,
                    mensaje.valor,
                );
            }

            if (mensaje.tipo === "ACTUALIZAR_ESTILO") {
                setConfig((prev) => ({
                    ...prev,
                    bloquesHome: prev.bloquesHome.map((b) => {
                        if (
                            !coincideBloque(
                                b,
                                mensaje.uniIdBloque,
                                mensaje.vchTipo,
                                mensaje.intOrden,
                            )
                        ) {
                            return b;
                        }
                        const valorCoercido = mensaje.campo.startsWith("int")
                            ? Number(mensaje.valor)
                            : mensaje.valor;
                        return {
                            ...b,
                            estilo: { ...b.estilo, [mensaje.campo]: valorCoercido },
                        };
                    }),
                }));
                return;
            }

            if (mensaje.tipo === "ACTUALIZAR_FAQS") {
                setConfig((prev) => ({ ...prev, faqs: mensaje.faqs }));
            }

            if (mensaje.tipo === "SOLICITAR_CONTENIDO") {
                const { intVersionEsquema, bloquesHome, identidad, faqs, faqsConfig } =
                    configRef.current;
                const contenido: TiendaContenidoDTO = {
                    intVersionEsquema,
                    identidad,
                    bloquesHome,
                    faqs,
                    faqsConfig,
                };
                enviarMensajeAlAdministrador(
                    crearMensajeEditor<ContenidoActualMensaje>({
                        tipo: "CONTENIDO_ACTUAL",
                        contenido,
                        vchSolicitudId: mensaje.vchSolicitudId,
                    }),
                    vchOrigenPadre,
                );
            }
        };

        window.addEventListener("message", handleMessage);
        enviarMensajeAlAdministrador(
            crearMensajeEditor<EditorListoMensaje>({
                tipo: "EDITOR_LISTO",
                bitContenidoMigrado: configInicial.bitContenidoMigrado,
            }),
            vchOrigenPadre,
        );

        return () => window.removeEventListener("message", handleMessage);
    }, [bitContentEditMode, actualizarCampo, vchOrigenPadre, configInicial.bitContenidoMigrado]);

    return (
        <TemplateConfigContext.Provider value={{ config, bitContentEditMode }}>
            {children}
        </TemplateConfigContext.Provider>
    );
};

export function useTemplateConfig() {
    const value = useContext(TemplateConfigContext);
    if (!value) {
        throw new Error("useTemplateConfig debe usarse dentro de TemplateConfigProvider");
    }
    return value;
}

export function useEditableBlock(bloque: BloqueHomeDTO) {
    const { config, bitContentEditMode } = useTemplateConfig();

    const emitirSeleccion = useCallback(() => {
        if (!bitContentEditMode) return;
        const campos = derivarCamposEditables(bloque.config, bloque.vchTipo, bloque.bitActivo);
        const valores: Record<string, string> = {
            bitActivo: String(bloque.bitActivo),
        };
        for (const campo of campos) {
            const val = (bloque.config as Record<string, unknown>)[campo.key];
            if (val != null) {
                valores[campo.key] = String(val);
            } else if (campo.key === "vchImagenes" && bloque.config.vchImagen) {
                valores[campo.key] = JSON.stringify([bloque.config.vchImagen]);
            }
        }
        const opcionesColor = [
            { valor: config.theme.colores.vchMarcaPrincipal, label: "Marca principal" },
            { valor: config.theme.colores.vchMarcaSecundaria, label: "Marca secundaria" },
            { valor: config.theme.colores.vchFondoPagina, label: "Fondo principal" },
            { valor: config.theme.colores.vchFondoSuperficie, label: "Superficie" },
            { valor: config.theme.colores.vchTextoPrincipal, label: "Texto principal" },
            { valor: config.theme.colores.vchTextoSecundario, label: "Texto secundario" },
            { valor: config.theme.botones.vchPrimaryBg, label: "Botón principal" },
            { valor: config.theme.botones.vchPrimaryTexto, label: "Texto de botón" },
        ].filter(
            (opcion, intIndice, opciones) =>
                opciones.findIndex((candidata) => candidata.valor === opcion.valor) === intIndice,
        );
        const camposEstilo = derivarCamposEstilo(bloque.vchTipo, opcionesColor);
        const valoresEstilo: Record<string, string> = {};
        for (const campo of camposEstilo) {
            const val = (bloque.estilo as Record<string, unknown> | undefined)?.[campo.key];
            if (val != null) valoresEstilo[campo.key] = String(val);
        }
        enviarMensajeAlAdministrador(
            crearMensajeEditor<BloqueSeleccionadoMensaje>({
                tipo: "BLOQUE_SELECCIONADO",
                uniIdBloque: bloque.uniIdBloque,
                vchTipo: bloque.vchTipo,
                intOrden: bloque.intOrden,
                campos,
                valores,
                camposEstilo,
                valoresEstilo,
            }),
            obtenerOrigenPadre(),
        );
    }, [bitContentEditMode, bloque, config.theme]);

    return { bitContentEditMode, emitirSeleccion };
}

export function useEditableIdentidad() {
    const { config, bitContentEditMode } = useTemplateConfig();

    const emitirSeleccion = useCallback(() => {
        if (!bitContentEditMode) return;
        const valores: Record<string, string> = {};
        for (const campo of CAMPOS_IDENTIDAD) {
            const val = (config.identidad as Record<string, unknown>)[campo.key];
            if (val != null) valores[campo.key] = String(val);
        }
        enviarMensajeAlAdministrador(
            crearMensajeEditor<BloqueSeleccionadoMensaje>({
                tipo: "BLOQUE_SELECCIONADO",
                uniIdBloque: VCHTYPE_IDENTIDAD,
                vchTipo: VCHTYPE_IDENTIDAD,
                intOrden: -1,
                campos: CAMPOS_IDENTIDAD,
                valores,
                camposEstilo: [],
                valoresEstilo: {},
            }),
            obtenerOrigenPadre(),
        );
    }, [bitContentEditMode, config.identidad]);

    return { bitContentEditMode, emitirSeleccion };
}

const CAMPOS_FAQS_PAGE: CampoEditable[] = [
    { key: "vchEyebrow", label: "Eyebrow", tipo: "texto" },
    { key: "vchTitulo", label: "Título", tipo: "texto" },
    { key: "vchSubtitulo", label: "Subtítulo", tipo: "textarea" },
    { key: "vchLabelMenu", label: "Nombre en el menú", tipo: "texto" },
    { key: "bitVisible", label: "Mostrar en el menú", tipo: "toggle" },
];

export function useEditableFaqsPage() {
    const { config, bitContentEditMode } = useTemplateConfig();

    useEffect(() => {
        if (!bitContentEditMode) return;
        const fc = config.faqsConfig ?? {};
        const valores: Record<string, string> = {
            vchEyebrow: fc.vchEyebrow ?? "Preguntas frecuentes",
            vchTitulo: fc.vchTitulo ?? "¿En qué podemos ayudarte?",
            vchSubtitulo:
                fc.vchSubtitulo ??
                "Encontrá respuestas a las preguntas más frecuentes sobre nuestros productos y servicios.",
            vchLabelMenu: fc.vchLabelMenu ?? "FAQs",
            bitVisible: String(fc.bitVisible ?? true),
        };
        enviarMensajeAlAdministrador(
            crearMensajeEditor<BloqueSeleccionadoMensaje>({
                tipo: "BLOQUE_SELECCIONADO",
                uniIdBloque: "faqs",
                vchTipo: "faqs",
                intOrden: -99,
                campos: CAMPOS_FAQS_PAGE,
                valores,
                camposEstilo: [],
                valoresEstilo: {},
            }),
            obtenerOrigenPadre(),
        );
        return () => {
            enviarMensajeAlAdministrador(
                crearMensajeEditor<BloqueDeseleccionadoMensaje>({
                    tipo: "BLOQUE_DESELECCIONADO",
                }),
                obtenerOrigenPadre(),
            );
        };
        // solo al montar en modo edición, no en cada cambio de faqsConfig
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bitContentEditMode]);

    return { bitContentEditMode };
}
